import React from 'react';
import { Drawer, List, Tag, Typography, Space, Alert, Empty, Spin } from 'antd';
import { 
  CheckCircleOutlined, 
  SyncOutlined, 
  VideoCameraOutlined, 
  FileTextOutlined, 
  ClockCircleOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { SourceChapter, ChapterStatus } from '../../types';

const { Text, Title } = Typography;

interface ChapterProcessDrawerProps {
  visible: boolean;
  onClose: () => void;
  chapter: SourceChapter | null;
  chapterStatus: ChapterStatus | null;
  loading?: boolean;
}

const ChapterProcessDrawer: React.FC<ChapterProcessDrawerProps> = ({
  visible,
  onClose,
  chapter,
  chapterStatus,
  loading = false,
}) => {
  return (
    <Drawer
      title={chapter ? `Chapter: ${chapter.name || `#${chapter.orderIndex}`}` : 'Chapter Details'}
      placement="right"
      onClose={onClose}
      open={visible}
      width={450}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spin tip="Loading status..." />
        </div>
      ) : (
        <>
          {chapterStatus?.activeTasks && chapterStatus.activeTasks.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <Title level={5}>Active Tasks</Title>
              {chapterStatus.activeTasks.map(task => (
                <Alert
                  key={task.id}
                  message={
                    <Space>
                      {task.status === 'IN_PROGRESS' ? <SyncOutlined spin /> : <ClockCircleOutlined />}
                      <Text strong>{task.type}</Text>
                      <Tag color={task.status === 'IN_PROGRESS' ? 'processing' : 'default'}>
                        {task.status}
                      </Tag>
                    </Space>
                  }
                  type="info"
                  style={{ marginBottom: 8 }}
                />
              ))}
            </div>
          )}

          <div>
            <Title level={5}>Processes</Title>
            {chapterStatus?.processes && chapterStatus.processes.length > 0 ? (
              <List
                dataSource={chapterStatus.processes}
                renderItem={(process) => (
                  <List.Item>
                    <Card
                      size="small"
                      style={{ width: '100%', borderRadius: 8 }}
                      title={
                        <Space>
                          <Text strong>{process.name}</Text>
                          <Tag color={process.videoForm === 'LONG' ? 'blue' : 'purple'}>
                            {process.videoForm}
                          </Tag>
                        </Space>
                      }
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Space>
                            <FileTextOutlined style={{ color: process.hasEpisode ? '#52c41a' : '#d9d9d9' }} />
                            <Text>Episode</Text>
                          </Space>
                          {process.hasEpisode ? (
                            <Tag color="success" icon={<CheckCircleOutlined />}>Created</Tag>
                          ) : (
                            <Tag color="default">Not Created</Tag>
                          )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Space>
                            <VideoCameraOutlined style={{ color: process.hasVideo ? '#52c41a' : '#d9d9d9' }} />
                            <Text>Video</Text>
                          </Space>
                          {process.hasVideo ? (
                            <Tag color="success" icon={<CheckCircleOutlined />}>Generated</Tag>
                          ) : (
                            <Tag color="default">Not Generated</Tag>
                          )}
                        </div>
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              !chapterStatus?.activeTasks?.length && <Empty description="No processes found for this chapter" />
            )}
          </div>
        </>
      )}
    </Drawer>
  );
};

// Helper component for Process item inside List
const Card = ({ children, style, title, size }: any) => (
  <div style={{ 
    border: '1px solid #f0f0f0', 
    borderRadius: 8, 
    width: '100%',
    ...style 
  }}>
    <div style={{ 
      padding: '8px 12px', 
      borderBottom: '1px solid #f0f0f0',
      backgroundColor: '#fafafa',
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      {title}
    </div>
    <div style={{ padding: '12px' }}>
      {children}
    </div>
  </div>
);

export default ChapterProcessDrawer;
